import * as React from "react";
import { Button, Text, Spinner, Link, makeStyles, tokens } from "@fluentui/react-components";
import type { AuthStatus } from "./useAuth";
import { getAdminConsentUrl } from "./authDiagnostics";
import { authConfig } from "./authConfig";
import { useTranslation } from "react-i18next";

interface SignInScreenProps {
  status: AuthStatus;
  error?: string;
  /** Reason silent session restore failed, if it did. */
  diagnostic?: string;
  onSignIn: () => void;
}

const useStyles = makeStyles({
  container: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "90vh",
    padding: "24px",
    gap: "12px",
    textAlign: "center",
  },
  hero: {
    width: "100%",
    maxWidth: "220px",
    height: "auto",
    marginBottom: "8px",
  },
  heading: {
    color: tokens.colorNeutralForeground3,
  },
  subHeading: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  signInGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    marginTop: "12px",
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  button: {
    width: "100%",
    maxWidth: "320px",
  },
  // Matches the button and the consent block, so a wrapped message lines up with
  // everything else on the screen instead of sitting flush left.
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    maxWidth: "320px",
    marginTop: "8px",
    textAlign: "center",
  },
  diagnostic: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase100,
    marginTop: "4px",
  },
  // Shown when the organisation has never approved the add-in. Deliberately not
  // styled as an error: nothing is broken and the user has done nothing wrong —
  // someone else has to act, and this tells them who and how.
  consent: {
    marginTop: "12px",
    padding: "12px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    textAlign: "left",
    maxWidth: "320px",
  },
  consentTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  consentText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
});

const SignInScreen: React.FC<SignInScreenProps> = ({ status, error, diagnostic, onSignIn }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const consentUrl = getAdminConsentUrl(authConfig.clientId);

  return (
    <div className={styles.container}>
      <img className={styles.hero} src="/assets/sms-hero.svg" alt="LOX24 SMS Gateway" />
      {/* align="center" on every Text here: Fluent's Text sets text-align:start
          on itself, so the container's centring only *looks* applied while a line
          fits — a longer translation that wraps falls back to flush left. */}
      <Text align="center" className={styles.heading}>
        {t("auth.signIn.tagline")}
      </Text>
      <Text align="center" className={styles.subHeading}>
        {t("auth.signIn.product")}
      </Text>

      {status === "checking" || status === "signingIn" ? (
        <Spinner
          size="small"
          label={status === "checking" ? t("auth.signIn.loading") : t("auth.signIn.signingIn")}
        />
      ) : (
        <div className={styles.signInGroup}>
          <Button className={styles.button} appearance="primary" size="large" onClick={onSignIn}>
            {t("auth.signIn.button")}
          </Button>
          <Text align="center" className={styles.subtitle}>
            {t("auth.signIn.subtitle")}
          </Text>
          {diagnostic && (
            <Text align="center" className={styles.diagnostic}>
              {diagnostic}
            </Text>
          )}
          {consentUrl && (
            <div className={styles.consent}>
              <Text className={styles.consentTitle}>{t("auth.consent.title")}</Text>
              <Text className={styles.consentText}>{t("auth.consent.body")}</Text>
              <Link href={consentUrl} target="_blank" rel="noreferrer">
                {t("auth.consent.link")}
              </Link>
              <Text className={styles.consentText}>{t("auth.consent.after")}</Text>
            </div>
          )}
        </div>
      )}

      {status === "error" && error && <Text className={styles.error}>{error}</Text>}
    </div>
  );
};

export default SignInScreen;
